import type { ComponentRuntimeDefinition } from '../../catalog/index.ts';
import { z } from 'zod';
import { componentHardMinimum } from '../shared/sizingProfiles.ts';

const parameterSliderConfigSchema = z.object({
  label: z.string().default('参数'),
  runtimeId: z.string().min(1),
  parameter: z.string().min(1),
  min: z.number().finite(),
  max: z.number().finite(),
  step: z.number().positive(),
  value: z.number().finite(),
  unit: z.string().default(''),
  updateMode: z.enum(['immediate', 'commit']).default('immediate'),
  showNumberInput: z.boolean().default(true),
  showReset: z.boolean().default(true),
  discrete: z.boolean().default(false),
  logarithmic: z.boolean().default(false),
  orientation: z.enum(['horizontal', 'vertical']).default('horizontal'),
}).strict().refine(value => value.max > value.min, {
  message: '最大值必须大于最小值',
}).refine(value => value.value >= value.min && value.value <= value.max, {
  message: '初始值必须位于参数范围内',
});

const actionItemSchema = z.object({
  label: z.string().min(1).max(40),
  actionId: z.string().min(1).max(80),
  variant: z.enum(['primary', 'secondary', 'danger']).default('secondary'),
  confirmLabel: z.string().min(1).max(40).optional(),
  allowRepeat: z.boolean().default(true),
  eventType: z.string().min(1).max(80).optional(),
}).strict();

const playbackConfigSchema = z.object({
  runtimeId: z.string().min(1),
  showPlay: z.boolean().default(true),
  showStep: z.boolean().default(true),
  showReset: z.boolean().default(true),
  showSpeed: z.boolean().default(true),
  stepLabel: z.string().min(1).max(40).default('单步运行'),
  speeds: z.array(z.number().positive().max(100)).min(1).max(12).default([0.25, 0.5, 1, 2, 4]),
}).strict();

export const parameterSliderDefinition: ComponentRuntimeDefinition = {
  descriptor: {
    key: 'probfun.parameter-slider@1',
    category: 'control',
    title: '参数滑块',
    description: '以结构化交互事件更新运行时数值参数。',
    capabilities: ['parameter-control', 'runtime-interaction', 'reset-default'],
    suitableFor: ['概率参数', '速率', '阈值', '数值调节'],
    inputs: [],
    outputs: [],
    events: [{
      type: 'parameter.changed',
      description: '参数值发生变化',
    }],
    constraints: ['只发送结构化参数事件'],
    configSchemaId: 'probfun.parameter-slider.config@1',
    configSchema: {
      type: 'object',
      required: ['runtimeId', 'parameter', 'min', 'max', 'step', 'value'],
      properties: {
        label: { type: 'string', default: '参数' },
        runtimeId: { type: 'string', minLength: 1 },
        parameter: { type: 'string', minLength: 1 },
        min: { type: 'number' },
        max: { type: 'number' },
        step: { type: 'number', exclusiveMinimum: 0 },
        value: { type: 'number' },
        unit: { type: 'string', default: '' },
        updateMode: {
          type: 'string',
          enum: ['immediate', 'commit'],
          default: 'immediate',
        },
        showNumberInput: { type: 'boolean', default: true },
        showReset: { type: 'boolean', default: true },
        discrete: { type: 'boolean', default: false },
        logarithmic: { type: 'boolean', default: false },
        orientation: {
          type: 'string',
          enum: ['horizontal', 'vertical'],
          default: 'horizontal',
        },
      },
      additionalProperties: false,
    },
    defaultConfig: {
      label: '参数',
      runtimeId: 'main-runtime',
      parameter: 'value',
      min: 0,
      max: 10,
      step: 0.1,
      value: 1,
      unit: '',
      updateMode: 'immediate',
      showNumberInput: true,
      showReset: true,
      discrete: false,
      logarithmic: false,
      orientation: 'horizontal',
    },
    examples: [{
      label: '到达率 λ',
      runtimeId: 'queue-runtime',
      parameter: 'arrivalRate',
      min: 0.1,
      max: 0.95,
      step: 0.05,
      value: 0.6,
      unit: ' 人/秒',
      updateMode: 'immediate',
      showNumberInput: true,
      showReset: true,
      discrete: false,
      logarithmic: false,
      orientation: 'horizontal',
    }],
    notes: ['滑块只发送结构化参数事件，不直接修改 Runtime 状态。', 'immediate 模式拖动时连续发送；commit 模式松开滑块才发送。', 'discrete 模式下步进为 1 的整数步。', '滑块轨道始终横向；窄而高时数值改到标签下方，宽而矮时标签和数值改到轨道左侧。'],
    layout: {
      ...componentHardMinimum('probfun.parameter-slider@1'),
      maxHeight: 240,
      maxWidth: 600,
      preferredAspectRatio: 3,
    },
    runtimeRequirements: [{
      capability: 'parameter-update',
      required: true,
      description: '运行时应接受 parameters.patch 命令',
    }],
    metadata: {
      layoutHints: {
        roles: ['controls'],
        preferredSize: 'small',
        supportsCompact: true,
        supportsExpanded: true,
        canStack: true,
        priority: 60,
      },
    },
  },
  configSchema: parameterSliderConfigSchema,
  loader: () => import('./ParameterSliderBlock.vue').then(module => module.default),
};

export const playbackControlDefinition: ComponentRuntimeDefinition = {
  descriptor: {
    key: 'probfun.playback-control@1',
    category: 'control',
    title: '播放控制',
    description: '启动、暂停、继续、步进和重置运行会话。',
    capabilities: ['runtime-control'],
    suitableFor: ['仿真过程', '分步教学'],
    inputs: [],
    outputs: [],
    events: [
      { type: 'playback.start', description: '开始运行' },
      { type: 'playback.pause', description: '暂停运行' },
      { type: 'playback.resume', description: '继续运行' },
      { type: 'playback.reset', description: '重置运行' },
      { type: 'playback.step', description: '单步运行' },
      { type: 'playback.speed.changed', description: '修改运行速度' },
    ],
    constraints: ['必须指向已声明的运行会话'],
    configSchemaId: 'probfun.playback-control.config@1',
    configSchema: {
      type: 'object',
      required: ['runtimeId'],
      properties: {
        runtimeId: { type: 'string', minLength: 1 },
        showPlay: { type: 'boolean', default: true },
        showStep: { type: 'boolean', default: true },
        showReset: { type: 'boolean', default: true },
        showSpeed: { type: 'boolean', default: true },
        stepLabel: {
          type: 'string',
          minLength: 1,
          maxLength: 40,
          default: '单步运行',
        },
        speeds: {
          type: 'array',
          minItems: 1,
          maxItems: 12,
          items: {
            type: 'number',
            exclusiveMinimum: 0,
            maximum: 100,
          },
        },
      },
      additionalProperties: false,
    },
    defaultConfig: {
      runtimeId: 'main-runtime',
      showPlay: true,
      showStep: true,
      showReset: true,
      showSpeed: true,
      stepLabel: '单步运行',
      speeds: [0.25, 0.5, 1, 2, 4],
    },
    examples: [{
      runtimeId: 'queue-runtime',
      showPlay: true,
      showStep: true,
      showReset: true,
      showSpeed: true,
      stepLabel: '推进到下一事件',
      speeds: [0.25, 0.5, 1, 2, 4],
    }, {
      runtimeId: 'queue-runtime',
      showPlay: true,
      showStep: false,
      showReset: true,
      showSpeed: false,
      stepLabel: '单步运行',
      speeds: [1],
    }],
    notes: [
      '播放控制通过 InteractionEvent 与 Runtime 通信。',
      '可用 showPlay / showStep / showReset / showSpeed 按需显示功能。',
      '空格键切换播放/暂停（需 showPlay），R 键重置（需 showReset）。',
      'compact 模式下只显示图标，但仍尊重显隐配置。',
      'compact 下仅当横版会溢出、竖版仍能完整放下时改竖排；其余情况保持横版。',
      '「点一下做一次」的用户动作请用 probfun.action-button@1，不要写进播放条。',
    ],
    layout: {
      ...componentHardMinimum('probfun.playback-control@1'),
      maxHeight: 180,
      maxWidth: 700,
    },
    runtimeRequirements: [{
      capability: 'playback-control',
      required: true,
      description: '运行时应实现完整播放生命周期',
    }],
    metadata: {
      layoutHints: {
        roles: ['controls'],
        preferredSize: 'small',
        supportsCompact: true,
        supportsExpanded: false,
        canStack: true,
        priority: 70,
      },
    },
  },
  configSchema: playbackConfigSchema,
  loader: () => import('./PlaybackControlBlock.vue').then(module => module.default),
};

const actionButtonConfigSchema = z.object({
  label: z.string().min(1).max(40).default('执行'),
  actionId: z.string().min(1).max(80).default('action'),
  runtimeId: z.string().min(1).optional(),
  variant: z.enum(['primary', 'secondary', 'danger']).default('primary'),
  description: z.string().max(200).optional(),
  confirmLabel: z.string().min(1).max(40).optional(),
  allowRepeat: z.boolean().default(true),
  actions: z.array(actionItemSchema).max(12).optional(),
  helpTexts: z.array(z.string().max(200)).max(8).optional(),
}).strict();

export const actionButtonDefinition: ComponentRuntimeDefinition = {
  descriptor: {
    key: 'probfun.action-button@1',
    category: 'control',
    title: '操作按钮',
    description: '通用一次性或可重复的用户操作触发器（点一下，模型做一次）。',
    capabilities: ['action-trigger', 'interaction-trigger'],
    suitableFor: [
      '提交服务',
      '放入一个顾客',
      '抽一次',
      '掷一次',
      '锁定当前结果',
      '执行动作',
      '确认操作',
    ],
    inputs: [],
    outputs: [],
    events: [{
      type: 'action.triggered',
      description: '用户点击按钮，payload 含 actionId、label，可选 runtimeId',
      configurable: true,
    }],
    constraints: ['只发送结构化交互事件，不直接改 Runtime 状态'],
    configSchemaId: 'probfun.action-button.config@1',
    configSchema: {
      type: 'object',
      required: ['label', 'actionId'],
      properties: {
        label: { type: 'string', minLength: 1, maxLength: 40, default: '执行' },
        actionId: { type: 'string', minLength: 1, maxLength: 80 },
        runtimeId: { type: 'string', minLength: 1 },
        variant: {
          type: 'string',
          enum: ['primary', 'secondary', 'danger'],
          default: 'primary',
        },
        description: { type: 'string', maxLength: 200 },
        confirmLabel: { type: 'string', minLength: 1, maxLength: 40 },
        allowRepeat: { type: 'boolean', default: true },
        actions: {
          type: 'array',
          maxItems: 12,
          items: {
            type: 'object',
            required: ['label', 'actionId'],
            properties: {
              label: { type: 'string', minLength: 1, maxLength: 40 },
              actionId: { type: 'string', minLength: 1, maxLength: 80 },
              variant: {
                type: 'string',
                enum: ['primary', 'secondary', 'danger'],
                default: 'secondary',
              },
              confirmLabel: { type: 'string', maxLength: 40 },
              allowRepeat: { type: 'boolean', default: true },
              eventType: { type: 'string', minLength: 1, maxLength: 80 },
            },
            additionalProperties: false,
          },
        },
        helpTexts: {
          type: 'array',
          maxItems: 8,
          items: { type: 'string', maxLength: 200 },
        },
      },
      additionalProperties: false,
    },
    defaultConfig: {
      label: '执行',
      actionId: 'action',
      variant: 'primary',
      allowRepeat: true,
    },
    examples: [{
      label: '放入顾客',
      actionId: 'arrive-customer',
      runtimeId: 'queue-runtime',
      variant: 'secondary',
      allowRepeat: true,
      actions: [{
        label: '放入顾客',
        actionId: 'arrive-customer',
        variant: 'secondary',
        allowRepeat: true,
        eventType: 'customer.arrive',
      }, {
        label: '提交服务',
        actionId: 'submit-service',
        variant: 'primary',
        allowRepeat: true,
        eventType: 'service.submit',
      }],
      helpTexts: [
        '放入顾客：手动让一位顾客进入系统，与自动到达相互独立',
        '提交服务：手动完成当前正在服务的顾客，队伍为空时忽略',
      ],
    }, {
      label: '放入一个顾客',
      actionId: 'arrive-customer',
      runtimeId: 'queue-runtime',
      variant: 'secondary',
      description: '手动让一个实体进入系统',
      allowRepeat: true,
    }, {
      label: '提交服务',
      actionId: 'submit-service',
      runtimeId: 'queue-runtime',
      variant: 'primary',
      description: '手动完成当前正在服务的顾客',
      allowRepeat: true,
    }, {
      label: '抽一次',
      actionId: 'sample-once',
      variant: 'primary',
      description: '从分布中抽取一个样本，或掷一次骰子/硬币',
      allowRepeat: true,
    }, {
      label: '收下这一次',
      actionId: 'lock-sample',
      variant: 'secondary',
      description: '锁定当前结果，记入样本后再继续',
      confirmLabel: '已收下',
      allowRepeat: true,
    }, {
      label: '确认开始',
      actionId: 'confirm-start',
      variant: 'secondary',
      allowRepeat: false,
      confirmLabel: '已确认',
    }],
    notes: [
      '发出 action.triggered，由 InteractionRouter / model.interaction 处理。',
      '同一组件换 label 与 actionId 即可表达放入、提交、抽一次、锁定等动作；也可用 actions 在一张卡片里放 1–12 个按钮。',
      '多按钮按容器宽高自动选行列：尽量接近正方形、优先单行放下文字，格子变窄再按外壳换行。',
      '按钮本身不改模型；页面 Block 需为每个动作声明 model.interaction。默认发 action.triggered；actions[].eventType 可改为模型交互名。',
      'helpTexts 的 i 出现在模块标题行右上角（与「互动操作」标题齐平）；点击或悬停出现浮窗。',
      'allowRepeat=false 时点击一次后禁用，文案切换为 confirmLabel。',
      'compact 下隐藏 description，保留按钮与帮助图标。',
      '高度先收到上下边距与按键相等，再按外壳形状换行；到达硬最小尺寸后不再整体缩小。',
      '宽或高不足会先藏次要再改横/竖排；到达硬最小尺寸后不再缩小、缩放或滚动。',
    ],
    layout: {
      ...componentHardMinimum('probfun.action-button@1'),
      maxWidth: 420,
      maxHeight: 220,
    },
    runtimeRequirements: [],
    metadata: {
      layoutHints: {
        roles: ['controls'],
        preferredSize: 'small',
        supportsCompact: true,
        supportsExpanded: true,
        canStack: true,
        priority: 65,
      },
    },
  },
  configSchema: actionButtonConfigSchema,
  loader: () => import('./ActionButtonBlock.vue').then(module => module.default),
};
